import { h, Component } from 'preact';
import isShallowEqual from 'shallow-equals';
import Store from 'store/store';
import * as actions from 'store/actions';
import { accessNested, joinClasses } from 'utils/utils';
import { requestSignedStaticMapUrl } from 'utils/clientUtils';
import { getCopy } from 'utils/copy';
import { track } from 'utils/tracking';
import { Link } from 'components/core/link/link.js';
import { SvgTrain, SvgCar, SvgGuy, SvgLinkOut, SvgCompass } from 'components/shared/svgIcons/svgIcons';
import CustomMarker from 'components/shared/gmap/customMarker.js';
import GMapMap from 'components/shared/gmap/gmapMap';
import DrawableMarker from 'components/shared/gmap/drawableMarker';
import s from 'components/shared/gmap/gmap.sss';

import { clamp } from 'utils/math';

const cityDrawingsCache = {};
// Keep that outside to have a persistent value between mounts
let received = false;

const HTML_COLOR_REG = /^#([0-9a-f]{3}){1,2}$/i;
function colorHtmlToHex(str) {
	let d = str.substr(1);
	if (d.length === 3) {
		d = d[0] + d[0] + d[1] + d[1] + d[2] + d[2];
	}
	return '0x' + d.toLowerCase();
}
function googleMapStyleToQueryString(rules) {
	if (!Array.isArray(rules)) return [];
	return rules.map(rule => {
		let parts = [];
		let add = (key, val) => key && val && parts.push(key + ':' + val);
		add('feature', rule.featureType);
		add('element', rule.elementType);
		if (Array.isArray(rule.stylers)) {
			rule.stylers.forEach(style => {
				Object.keys(style).forEach(key => {
					let value = style[key];
					if (HTML_COLOR_REG.test(value)) {
						value = colorHtmlToHex(value);
					}
					add(key, value);
				});
			});
		}
		return parts.join('|');
	});
}

export default class GMap extends Component {

	constructor(props) {
		super(props);

		this.received = received;
		this.requested = false;
		this.gmap = null;
		this.theaterMarkersLoaded = false;
		this.theaterMarkers = [];
		this.$theaterMarkers = [];
		this.cityMarkers = [];
		this.automount = !props.useStaticPreview;
		this.unmounted = false;
		this.loaded = false;

		this.state.travelMode = 'DRIVING';
		this.state.directionError = false;
		this.state.directionsLink = false;
		this.state.requestDirections = false;

		this.initGMaps = this.initGMaps.bind(this);
		this.CustomMarkerComp = CustomMarker;
		// this.retrySignature = this.retrySignature.bind(this);
	}

	componentDidMount() {
		if (received) {
			this.mountGMaps();
		} else if (this.automount) {
			this.initGMaps();
		}
	}

	componentWillReceiveProps(nextProps) {
		if (this.unmounted) return;

		if (!this.automount && !nextProps.useStaticPreview) {
			this.automount = true;
		}

		// Update theaters
		if (nextProps.theaters !== this.props.theaters) {
			this.updateTheaters(nextProps.theaters, nextProps.useCustomMarker);
		}

		// Update cities
		if (nextProps.cities !== this.props.cities) {
			this.updateCities(nextProps.cities);
		}

		// Update direcitons
		if (this.directionsMarker && nextProps.client.geo !== this.props.client.geo) {
			this.directionsMarker.setPosition(this.getCoordinatesForMap(nextProps.client.geo));
			this.updateDirections(nextProps.activeTheater);
		} else if (nextProps.activeTheater !== this.props.activeTheater) {
			this.updateDirections(nextProps.activeTheater);
		}

		// Set current position
		if (this.gmap && nextProps.center !== this.props.center) {
			let latLng = this.getCoordinatesForMap(nextProps.center);
			this.gmap.setCenter(latLng);
		}

		// Set user marker
		if (this.$userMarker && nextProps.marker !== this.props.marker) {
			if (!nextProps.marker) {
				this.$userMarker.setMap(null);
			} else {
				const nextPos = new google.maps.LatLng(nextProps.marker.lat, nextProps.marker.lon);
				this.$userMarker.setPosition(nextPos);
			}
		}
	}

	// We allow this component to update and use another inner component to contain the actual not updating map
	shouldComponentUpdate(nextProps, nextState) {
		if (this.unmounted) {
			this.unmounted = false;
			return true;
		}
		return !isShallowEqual(nextProps, this.props) || !isShallowEqual(nextState, this.state);
	}

	componentDidUpdate(prevProps) {
		if (!this.requested && this.automount) {
			this.initGMaps();
			this.$gmap.style.opacity = 1;
		}

		// Ensure theater markers dom order (to be able to use :nth-child in the CSS)
		if (prevProps.theaters) {
			const different = !!prevProps.theaters.find((t, i) => t.id !== (this.props.theaters[i] && this.props.theaters[i].id));
			if (different) {
				this.$theaterMarkers.forEach(m => m.refresh());
			}
		}

		if (this.$theaterMarkersContainer) {
			// Assumes a square static map image
			let parent = this.$theaterMarkersContainer.parentElement;
			let size = Math.max(parent.clientWidth, parent.clientHeight);
			this.$theaterMarkersContainer.style.width = size + 'px';
			this.$theaterMarkersContainer.style.height = size + 'px';
		}
	}

	componentWillUnmount() {
		this.unmounted = true;
	}

	mountGMaps() {
		if (!window.google || !google.maps) {
			return;
		}
		let center = this.getCoordinatesForMap(this.props.center);

		const options = {
			center: center,
			zoom: this.props.zoom || 12,
			clickableIcons: false,
			disableDefaultUI: true,
			mapTypeId: google.maps.MapTypeId.ROADMAP,
			mapTypeControlOptions: {
				mapTypeIds: [google.maps.MapTypeId.ROADMAP, 'map_style']
			},
			zoomControl: true,
			zoomControlOptions: {
				position: google.maps.ControlPosition.LEFT_BOTTOM
			}
		};

		let greedyGestures = true;
		if (accessNested(this.props, 'client.iframe') || this.props.ungreedy) {
			greedyGestures = false;
		}
		if (greedyGestures) {
			options.gestureHandling = 'greedy';
		}

		track({ a: 'map-load' });
		this.gmap = new google.maps.Map(this.$gmap, options);
		if (!this.props.unstyled) {
			let mapStyles = this.getMapStyles();

			const styledMap = new google.maps.StyledMapType(mapStyles, { name: 'Styled Map' });
			this.gmap.mapTypes.set('map_style', styledMap);
			this.gmap.setMapTypeId('map_style');
		}

		// Events: idle, tilesloaded
		google.maps.event.addListenerOnce(this.gmap, 'tilesloaded', () => this.setState({ loaded: true }));

		// Tracking
		// this.gmap.addListener('click', e => track({ a: 'map-click' }));
		this.gmap.addListener('dragend', () => track({ a: 'map-drag' }));
		this.gmap.addListener('zoom_changed', () => track({ a: 'map-zoom-changed' }));

		const marker = this.props.marker;
		if (marker) {
			const pos = new google.maps.LatLng(marker.lat, marker.lon);
			const gMarker = new google.maps.Marker({
				map: this.gmap,
				position: pos,
				draggable: marker.draggable,
				visible: true
			});
			const events = marker.events || {};
			Object.keys(events).forEach(event => google.maps.event.addListener(gMarker, event, events[event]));
			this.$userMarker = gMarker;
		}

		if (this.props.theaters) {
			this.updateTheaters(this.props.theaters, this.props.useCustomMarker);
		}
		if (this.props.cities) {
			this.updateCities(this.props.cities);
		}

		this.updateExtra();

		if (typeof this.props.onMount === 'function') this.props.onMount(this.gmap);
		// We updated the theaters/cities markers, force rerender
		this.forceUpdate();
	}

	onStaticMapError() {
		Store.get().showtimes.staticMaps.set('error', true);
	}

	getCoordinatesForMap(geo) {
		if (!geo) {
			return;
		}
		return typeof geo.toGoogleLatLng === 'function' ? geo.toGoogleLatLng() : new google.maps.LatLng(geo.lat, geo.lon);
	}

	updateExtra() {

	}

	updateDirections(goalTh) {
		if (!window.google) return;
		if (!goalTh) return;
		if (!this.directionsMarker) return;

		const start = this.directionsMarker.getPosition();
		this.state.start = start;
		if (!start) return;
		const dest = this.getCoordinatesForMap(goalTh.geo);

		// logic for setting up the link out to google maps
		let link;
		const startLat = start && start.lat();
		const startLng = start && start.lng();
		const destLat = dest && dest.lat();
		const destLng = dest && dest.lng();
		const travelM = this.state.travelMode.toLowerCase();
		if (startLat && startLng && destLat && destLng) {
			link = `https://www.google.com/maps/dir/?api=1&origin=${startLat},${startLng}&destination=${destLat},${destLng}&travelmode=${travelM}`;
		}
		if (link) {
			this.setState({ directionsLink: link });
		}

		const request = {
			origin:      start,
			destination: dest,
			travelMode:  google.maps.TravelMode[this.state.travelMode]
		};

		let requestTimer;
		const doRequest = () => {
			clearTimeout(requestTimer);
			this.directionsService.route(request, (response, status) => {
				if (status == google.maps.DirectionsStatus.ZERO_RESULTS) {
					// Clear out the response
					this.directionsDisplay.setDirections(response);
					// TODO: reenable direction error again,
					// calling setState breaks the map for some reason
					// this.setState({ directionError: true });
				}

				if (status == google.maps.DirectionsStatus.OK) {
					// this.setState({ directionError: false });
					this.directionsDisplay.setDirections(response);
				} else if (status === google.maps.DirectionsStatus.OVER_QUERY_LIMIT) {
					requestTimer = setTimeout(doRequest, 2000);
				}
				this.directionsDisplay.setMap(this.gmap);
			});
		};

		doRequest();
	}

	getMapStyles() {
		let mapStyles = this.props.mapStyles || accessNested(this.props.appData, 'pages.showtimes.data.mapStyles', []);
		return (Array.isArray(mapStyles) ? mapStyles : Object.values(mapStyles)).filter(e => e);
	}

	getTheaterLogoSrc(theater) {
		const shade = accessNested(this.props, 'appData.styling.mapLogoShade') || accessNested(this.props, 'appData.styling.logoShade', 'dark');
		const theaterLogoFolder = accessNested(this.props, 'appData.urls.roots.sharedImages') + '/logos/' + shade;
		const theaterLogo = accessNested(theater, 'exhibitor.logo') || 'default.png';
		return theaterLogoFolder + '/' + theaterLogo;
	}

	panTo(geo) {
		this.gmap.panTo(this.getCoordinatesForMap(geo));
	}

	updateTheaters(theaters, useCustomMarker) {
		// if (!this.gmap) return;

		let bottomAnchor = accessNested(this.props, 'pageData.mapMarkerBottomAnchor');

		let getPoint;

		let isStatic = !this.gmap;
		// Static image: 640x640, scale 2
		let imageSize = 1280;
		let scale = 1, centerP;
		if (isStatic) {
			// Google magic formulas
			// I guess this is 256 tiles / 360 degrees
			let j = 256 / 360;
			// Same in radians
			let l = 256 / (2 * Math.PI);
			let zoom = (this.props.zoom || 12);
			scale = Math.pow(2, zoom);
			// Transform a lat/lon in some kind of flat x,y
			getPoint = pos => {
				let px = 128 + pos.lon * j;
				let sin = clamp(Math.sin(Math.PI * pos.lat / 180), -(1 - 1E-15), 1 - 1E-15);
				let py = 128 + .5 * Math.log((1 + sin) / (1 - sin)) * -l;
				return { x: px, y: py };
			};
			// Center point
			centerP = getPoint(this.props.center || { lat: 0, lon: 0 });
		}

		this.theaterMarkers = (theaters || []).map((theater, i) => {
			if (!theater || !theater.geo || !theater.enabled || !theater.activeExhibitor) {
				return;
			}

			if (!theater.filterPass && this.props.hideFilteredTheaters) {
				return;
			}

			if (theater.active && this.gmap) {
				if (!this.theaterMarkersLoaded) {
					this.gmap.setCenter(this.getCoordinatesForMap(theater.geo));
					this.theaterMarkersLoaded = true;
				} else {
					this.panTo(theater.geo);
				}
			}

			let onclick;
			if (theater.filteredScreenings.length) {
				onclick = (e) => {
					e.stopPropagation();
					track({ a: 'map-theater-click', l: theater.id });
					Store.emit(actions.SELECT_THEATER, theater.id, 'map', theater);
				};
			}

			let extraProps = { isStatic };
			if (isStatic) {
				let p = getPoint(theater.geo);
				// Yeah whatever
				let posX = (p.x - centerP.x) * (scale / imageSize) + .5;
				let posY = (p.y - centerP.y) * (scale / imageSize) + .5;
				extraProps.style = {
					left: (100 * posX) + '%',
					top: (100 * posY) + '%',
				};
			}

			const CustomMarkerComp = this.CustomMarkerComp;
			return (
				<CustomMarkerComp
					useCustomMarker={useCustomMarker}
					bottomAnchor={bottomAnchor}
					key={theater.id}
					active={theater.active}
					// latlng={this.getCoordinatesForMap(theater.geo)}
					geo={theater.geo}
					map={this.gmap}
					onClick={onclick}
					class={!theater.filteredScreenings.length && s.disabledMarker}
					ref={e => e && (this.$theaterMarkers[i] = e)}
					{...extraProps}
				>
					{this.getTheaterMarkerContent(theater, i)}
				</CustomMarkerComp>
			);
		}).filter(e => e);
	}

	getTheaterMarkerContent(theater) {
		let logo = this.getTheaterLogoSrc(theater);
		if (!logo) {
			return;
		}
		return <img src={logo} key="logo" />;
	}

	drawCity(city, style) {
		// 2019-08-30: Disabled screening count as it was inaccurate
		// const count = city.screeningsCount;
		const count = '+';

		if (!cityDrawingsCache[count]) {
			const dest = document.createElement('canvas');
			const dctx = dest.getContext('2d');

			style = style || {};
			const {
				size         = 32,
				colorBack    = 'black',
				colorFront   = 'white',
				font         = '16px sans-serif',
				opacityBack  = 1,
				opacityFront = 1
			} = style;

			const hsize = size * .5;

			dest.width = dest.height = size;

			dctx.fillStyle = colorBack;
			dctx.globalAlpha = opacityBack;
			dctx.beginPath();
			dctx.arc(hsize, hsize, hsize, 0, Math.PI * 2);
			dctx.fill();

			dctx.textAlign = 'center';
			dctx.textBaseline = 'middle';
			dctx.font = font;
			dctx.fillStyle = colorFront;
			dctx.globalAlpha = opacityFront;
			dctx.fillText(count, hsize, hsize, size);

			cityDrawingsCache[count] = dest.toDataURL();
		}

		return cityDrawingsCache[count];
	}

	getCityStyle() {
		const styleVars = this.props.appData.styling.variables;

		return {
			size:       24,
			colorBack:  styleVars['screeningsPanel-btnBgColor'],
			colorFront: styleVars['screeningsPanel-btnTextColor'],
			font:       '16px ' + styleVars.defaultFont,
			opacityBack: .5,
			opacityFront: .5
		};
	}

	updateCities(cities) {
		if (!window.google) {
			return;
		}
		this.cityMarkers.forEach(dm => dm.remove());
		const style = this.getCityStyle();

		const searchRadiusFactor = +accessNested(this.props.appData, 'pages.showtimes.data.radiusFactor') || 1;
		// const searchRadiusFactor = +accessNested(this.props, 'pageData.radiusFactor') || 1;
		let searchRadius = searchRadiusFactor * 10;

		if (!cities) {
			return;
		}
		this.cityMarkers = cities.filter(city => city.distanceKm >= searchRadius).map(city => {
			const marker = new DrawableMarker(this.gmap, () => this.drawCity(city, style), this.getCoordinatesForMap(city.geo));

			marker.onClick = () => {
				track({ a: 'map-city-click', l: city.name });
				Store.emit(actions.SET_CLIENT_GEO, city.geo);
			};

			return marker;
		}).filter(e => e);
	}

	updateRequestDirections(requestDirections) {
		if (requestDirections && !this.directionsService && window.google) {
			this.directionsService = new google.maps.DirectionsService();

			this.directionsDisplay = new google.maps.DirectionsRenderer({
				draggable:       false,
				suppressMarkers: true
			});
			this.directionsDisplay.setOptions({ preserveViewport: true });

			const start = this.props.client.geo && this.getCoordinatesForMap(this.props.client.geo);
			this.directionsMarker = new google.maps.Marker({
				map:       this.gmap,
				position:  start,
				draggable: true,
				visible:   true
			});
			google.maps.event.addListener(this.directionsMarker, 'dragend', () => {
				this.updateDirections(this.props.activeTheater);
			});

			Store.emit(actions.MAP_REQUEST_DIRECTIONS, 'driving');
			track({ a: 'map-request-directions' });

			this.updateTravelMode('DRIVING', true);
		}

		this.setState({ requestDirections });
	}

	requestDirections = () => {
		this.updateRequestDirections(true);
	}

	updateTravelMode(travelMode, doNotTrack) {
		if (!doNotTrack) {
			track({ a: 'map-changed-travelmode', l: travelMode });
			Store.emit(actions.MAP_REQUEST_DIRECTIONS, travelMode);
		}
		this.setState({ travelMode });
		this.updateDirections(this.props.activeTheater);
	}

	initGMaps(e) {
		if (typeof window === 'undefined' || (window.google && window.google.maps) || this.requested) {
			// Google Maps is already initialized
			return;
		}
		this.requested = true;
		if (e && e.stopPropagation) {
			e.stopPropagation();
		}

		window.googleMapsLoaded = () => {
			this.received = received = true;
			this.mountGMaps();
		};

		const gmapsData = this.props.appData.apis.gmaps;
		const options = {
			key:       gmapsData.key,
			libraries: ['places','geometry'],
			callback:  'googleMapsLoaded',
			language:  gmapsData.language
		};

		const queryString = Object.keys(options).reduce((result, key) => (
			options[key] !== null && options[key] !== undefined ? (result += key + '=' + options[key] + '&') : result
		), '?').slice(0, -1);

		const script = document.createElement('script');
		script.onload = () => Store.emit(actions.GMAPS_LOADED);
		script.src = 'https://maps.googleapis.com/maps/api/js' + queryString;
		// script.src = 'https://static-maps.s-prod.pow.io/maps-script' + queryString;
		script.onerror = () => Store.emit(actions.API_ERROR, 'Google Maps', 'Error loading Google Maps script');
		document.head.appendChild(script);
	}

	renderDirections() {
		const props = this.props;
		const state = this.state;

		if (!props.useDirections) {
			return;
		}

		if (state.directionError) {
			return <div class={s.directionsFeedback} key="directions">{getCopy('NO_DIRECTIONS_FOUND')}</div>;
		}

		if (state.requestDirections) {
			// Already showing directions and no need to show the switcher
			if (!props.useMultipleDirections) return;

			const modes = [
				{ name: 'DRIVING', svg: SvgCar },
				{ name: 'TRANSIT', svg: SvgTrain },
				{ name: 'WALKING', svg: SvgGuy },
			];
			return (
				<div class={joinClasses(s.directionControls, props.activeTheater && s.show)} key="directions">
					{modes.map(mode => {
						const name = mode.name;
						const active = this.state.travelMode === name;
						const classname = joinClasses(s.directionControlItem, active && s.active, s[name.toLowerCase()]);
						const click = () => this.updateTravelMode(name);
						const Svg = mode.svg;
						return (
							<div onClick={click} class={classname}>
								<Svg class={s.directionIcon} />
							</div>
						);
					})}
					<Link href={this.state.directionsLink} target="_blank" class={s.linkToMaps} trackId='maps_directions_clicked'>
						<SvgLinkOut class={s.directionIcon} />
					</Link>
				</div>
			);
		} else {
			return (
				<div class={joinClasses(s.directionControls, props.activeTheater && s.show)} key="directions">
					<div class={s.requestDirections} onClick={this.requestDirections}>
						<SvgCompass class={s.directionIcon} />
						<span class={s.cta}>{getCopy('REQUEST_DIRECTIONS')}</span>
					</div>
				</div>
			);
		}
	}

	renderExtraMarkers() {}

	getStaticBackgroundURL() {
		let props = this.props;
		if (!props.center) {
			return;
		}
		let gmapsData = props.appData.apis.gmaps;
		let imageQS = [
			'maptype=roadmap',
			'zoom=' + ((props.zoom || 12) - 1),
			'size=640x640',
			'scale=2',
			// 'key=' + gmapsData.key,
			'center='+props.center.lat+','+props.center.lon
		];
		if (gmapsData.language) {
			imageQS.push('language=' + gmapsData.language);
		}
		let styles = this.getMapStyles() || [];
		imageQS = imageQS.concat(googleMapStyleToQueryString(styles).map(style => 'style=' + style));
		return 'https://maps.googleapis.com/maps/api/staticmap?' + imageQS.join('&');
	}

	getStaticBackground() {
		// if (this.staticBackground) return this.staticBackground;
		let url = this.getStaticBackgroundURL();
		if (!url) {
			return;
		}
		let allData = this.props.staticMaps || {};
		let data = allData[url];
		if (!data) {
			// Nothing yet: request for later
			requestSignedStaticMapUrl(url);
		}
		return data && data.signed;
	}

	// Retry button currently loading dynamic maps
	//   instead of reloading static maps then potentially loading dynamic maps later on
	//   (user is interested in the map)
	// retrySignature() {
	// 	let changes = { signError: false };
	// 	let url = this.getStaticBackgroundURL();
	// 	if (url) {
	// 		delete changes[url];
	// 	}
	// 	Store.get().showtimes.staticMaps.set(changes);
	// }

	render(props) {
		const {
			appData,
			center,
			useStaticPreview,
			staticMaps
		} = props;

		let isStatic = useStaticPreview && !this.gmap && center;
		let showLoader = useStaticPreview && !isStatic && !this.state.loaded;
		let staticSigningError = false;
		let backgroundImage;
		if (useStaticPreview) {
			if (staticMaps && staticMaps.signError) {
				staticSigningError = true;
				backgroundImage = (
					<div class={s.signingErrorMessage} key="signingError">
						Sorry, the map could not be loaded
						<button class={s.retry} onClick={this.initGMaps}>Retry</button>
					</div>
				);
			} else {
				let bgImage = this.getStaticBackground();
				if (bgImage) {
					backgroundImage = (
						<img
							class={s.background}
							src={bgImage}
							onError={this.onStaticMapError}
							key="staticMaps"
						/>
					);
				} else {
					showLoader = true;
				}
			}
		}

		return (
			<div
				class={joinClasses(s.container, props.class, isStatic && s.static, showLoader && s.loading, staticSigningError && s.staticSigningError)}
				onClick={isStatic && this.initGMaps}
				data-loading-text={appData.copy.$LOADING}
			>
				{backgroundImage}
				{this.renderDirections()}
				<GMapMap class={s.gmap} key="map" domRef={$ref => this.$gmap = $ref} />
				<div class={s.theaterMarkers} ref={e => this.$theaterMarkersContainer = e} key="markers">{this.theaterMarkers}</div>
				{this.renderExtraMarkers()}
			</div>
		);
	}

}
